import { FC, useMemo, useState } from 'react'
import classNames from 'classnames'
import Button from '@UI/New/Button'
import { IcoRemove } from '@/utils/icons'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'
import TooltipWrapper from '@pages/Admin/CourseSettings/components/TooltipWrapper/TooltipWrapper'
import EndDateWithProcessedCell from '@pages/Admin/Reports/ContractReport/components/EndDateWithProcessedCell/EndDateWithProcessedCell'
import EndDateWithProcessedColumnHeader from '@pages/Admin/Reports/ContractReport/components/EndDateWithProcessedCell/EndDateWithProcessedColumnHeader'
import SigningStateCell from '@pages/Admin/Reports/ContractReport/components/SigningStateCell/SigningStateCell'
import SendForSignatureModal from '@pages/Admin/Reports/ContractReport/components/SendForSignatureModal/SendForSignatureModal'
import {
  formatContractUploadedAt,
  formatSigningType,
  getContractFileName,
  getSignedStatusDetails,
} from '@pages/Admin/Reports/ContractReport/utils/contractSigningState.utils'
import {
  canSendContractForSignature,
  getSendForSignatureTooltip,
} from '@pages/Admin/Reports/ContractReport/utils/sendForSignature.utils'
import { usePodpislonReady } from '@pages/Admin/Reports/ContractReport/utils/usePodpislonReady'
import LegacyButton from '@UI/Button'
import { crmLeadContractApi } from '@/api'
import studentCn from '@pages/Admin/AllUsers/modals/EditStudentModal/components/StudentContractsTab/index.module.css'
import type { LeadContractDock } from './leadContracts.types'
import { useLeadContractActions } from './useLeadContractActions'
import classes from './index.module.css'

type LeadDocksListProps = {
  leadId: number
  contracts: LeadContractDock[]
  leadName?: string | null
  leadPhone?: string | null
  linkedUserId?: number | null
  parents?: Array<{
    id: number
    name?: string | null
    phone?: string | null
  }>
  onContractChanged: () => void | Promise<void>
  onDeleteDock: (dock: LeadContractDock) => void
}

const LeadDocksList: FC<LeadDocksListProps> = ({
  leadId,
  contracts,
  leadName,
  leadPhone,
  linkedUserId,
  parents = [],
  onContractChanged,
  onDeleteDock,
}) => {
  const { podpislonReady, isCheckingPodpislon } = usePodpislonReady()
  const [sendModalContract, setSendModalContract] =
    useState<LeadContractDock | null>(null)
  const [viewAll, setViewAll] = useState(false)

  const {
    contractLoading,
    handleContractEndDateChange,
    handleContractToggleProcessed,
    handleContractSigningStateChange,
  } = useLeadContractActions(leadId, onContractChanged)

  const contractsToShow = useMemo(
    () => (viewAll ? contracts : contracts.slice(0, 3)),
    [contracts, viewAll],
  )

  if (!contracts.length) {
    return (
      <div className={classes.emptyState}>
        <p className={classes.emptyStateTitle}>Договоров пока нет</p>
        <p className={classes.emptyStateHint}>
          Добавьте договор файлом или из шаблона
        </p>
      </div>
    )
  }

  return (
    <div className={studentCn.containerStudentDocks}>
      <div className={studentCn.containerStudentDocksList}>
        <div className={studentCn.docksListRow}>
          <div className={studentCn.docksListHeader}>
            <div className={classes.headerCell}>Дата и время загрузки</div>
            <EndDateWithProcessedColumnHeader className={classes.headerCell} />
            <div className={classes.headerCell}>Название документа</div>
            <div className={classes.headerCell}>Тип подписания</div>
            <div className={classes.headerCellCenter}>Файл</div>
            <div className={classes.headerCellCenter}>На подпись</div>
          </div>
          <div className={studentCn.deleteDockButtonPlaceholder} aria-hidden />
        </div>
        {contractsToShow.map((dock) => {
          const canSendForSignature = canSendContractForSignature(
            dock,
            podpislonReady,
            isCheckingPodpislon,
          )
          const sendForSignatureTooltip = getSendForSignatureTooltip(
            dock,
            podpislonReady,
            isCheckingPodpislon,
          )
          const hasStudentBinding = Boolean(
            dock.user_id != null && Number(dock.user_id) > 0,
          )
          const sendForSignatureButton = (
            <Button
              color="primary"
              disabled={!canSendForSignature}
              onClick={() => setSendModalContract(dock)}
            >
              На подпись
            </Button>
          )

          return (
            <div className={studentCn.docksListRow} key={`contract-${dock.id}`}>
              <div className={studentCn.docksListItem}>
                <div>{formatContractUploadedAt(dock)}</div>
                <EndDateWithProcessedCell
                  date={dock.contract_end_date ?? null}
                  processed={dock.is_processed}
                  dateLoading={
                    contractLoading?.id === dock.id &&
                    contractLoading?.field === 'endDate'
                  }
                  processedLoading={
                    contractLoading?.id === dock.id &&
                    contractLoading?.field === 'processed'
                  }
                  popupPlacement="topLeft"
                  closeOnScroll
                  onDateChange={(date) =>
                    handleContractEndDateChange(dock, date)
                  }
                  onToggleProcessed={() => handleContractToggleProcessed(dock)}
                />
                <div
                  className={classNames(
                    studentCn.documentNameCell,
                    classes.documentNameCell,
                  )}
                >
                  <span>{dock.name}</span>
                </div>
                <SigningStateCell
                  fixedSelectWidth
                  signingState={dock.signing_state}
                  signingStateLabel={dock.signing_state_label}
                  signingTypeLabel={formatSigningType(dock.signing_type)}
                  contractStatusHistory={dock.contract_status_history as any}
                  signedDetails={getSignedStatusDetails(dock)}
                  loading={
                    contractLoading?.id === dock.id &&
                    contractLoading?.field === 'signingState'
                  }
                  dropdownPlacement="topLeft"
                  closeOnScroll
                  onChange={(signingState) =>
                    handleContractSigningStateChange(dock, signingState)
                  }
                />
                <a
                  className={classNames(
                    studentCn.contractFileLink,
                    classes.truncateText,
                  )}
                  href={getCorrectFilePath(dock?.file_path ?? dock?.path)}
                  target="_blank"
                  rel="noreferrer"
                  title={getContractFileName(dock?.file_path ?? dock?.path)}
                >
                  {getContractFileName(dock?.file_path ?? dock?.path)}
                </a>
                <div className={studentCn.sendForSignatureButtonWrapper}>
                  {!canSendForSignature && sendForSignatureTooltip ? (
                    <TooltipWrapper
                      title={sendForSignatureTooltip}
                      style={{ width: '100%', display: 'flex' }}
                    >
                      <span
                        className={classNames(
                          studentCn.sendForSignatureButtonSlot,
                          studentCn.sendForSignatureButtonSlotDisabled,
                        )}
                      >
                        {sendForSignatureButton}
                      </span>
                    </TooltipWrapper>
                  ) : (
                    <span className={studentCn.sendForSignatureButtonSlot}>
                      {sendForSignatureButton}
                    </span>
                  )}
                </div>
              </div>
              <div className={studentCn.deleteDockButtonWrapper}>
                <div
                  className={studentCn.deleteDockButton}
                  onClick={() => onDeleteDock(dock)}
                >
                  <IcoRemove />
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {contracts.length > 3 && (
        <LegacyButton
          newDesign
          name=""
          className={classNames(classes.secondaryButton, classes.truncateText)}
          action={() => setViewAll((prev) => !prev)}
        >
          {viewAll ? 'скрыть' : 'смотреть еще'}
        </LegacyButton>
      )}
      <SendForSignatureModal
        open={!!sendModalContract}
        contract={
          sendModalContract
            ? {
                id: sendModalContract.id,
                name: sendModalContract.name,
                file_path:
                  sendModalContract.file_path ?? sendModalContract.path,
              }
            : null
        }
        student={{
          id: linkedUserId ?? undefined,
          name: leadName,
          phone: leadPhone,
          parents: linkedUserId ? parents : [],
        }}
        sendRequest={async (contractId, data) =>
          crmLeadContractApi.sendForSignature(
            { lead_id: leadId, id: contractId },
            data,
          )
        }
        onClose={() => setSendModalContract(null)}
        onSuccess={async () => {
          await onContractChanged()
        }}
      />
    </div>
  )
}

export default LeadDocksList
